import type { IntroCall } from "../lib/api";

/**
 * The introductory call, as a structure a person can follow out loud.
 *
 * **Why this is a constant and not a table.** Every consultant following the
 * same six stages is the entire point: it is what makes two calls comparable,
 * and what lets the firm ask "what did they actually say about the budget" of
 * forty prospects rather than forty notebooks. The moment this is editable per
 * user, two people are running two different calls and the record stops being
 * a record. If the firm wants to change the structure, that is a deliberate
 * change to this file, reviewed, not a form somebody fills in on a Tuesday.
 *
 * **Why it is a diagnostic and not a sales script.** The shape is the one that
 * recurs across discovery-call practice: get a *concrete recent instance* of
 * the problem before anything else, define the outcome, surface the
 * constraints, find out how the decision actually gets made, then read it back
 * and agree one next step. Two of the stages exist because of specific,
 * expensive failure modes:
 *
 *   * **Stage 5 is its own stage, not a line in the notes.** The stakeholder
 *     nobody mentions on the call is the one who blocks the recommendation two
 *     months in. "Who signs" and "who else is in the room" are separate
 *     questions with separate boxes so that leaving one empty is visible.
 *
 *   * **Stage 4 asks for a range, with undecided as a real answer.** A budget
 *     captured at intake is the baseline every later scope argument refers
 *     back to. But a number somebody invented to fill a required field is
 *     worse than no number, because it then looks like something the client
 *     said.
 *
 * The `prompts` are written to be read aloud or glanced at mid-call. They are
 * deliberately questions, not labels.
 */

/** A field on the call, and how it is captured. */
export interface OutlineField {
  /** The key on `IntroCall`. Typed, so a renamed column breaks the build. */
  key: keyof IntroCall;
  label: string;
  /** `long` gets a textarea; the call is mostly prose. */
  kind: "short" | "long" | "date";
  hint?: string;
  /** One of the four the database requires before a call may be completed. */
  required?: true;
}

export interface OutlineStage {
  /** 1–6. Shown, because knowing where you are in a call matters. */
  number: number;
  title: string;
  /** What this stage is for, in one line, for whoever is running the call. */
  purpose: string;
  /** Questions to ask, verbatim. */
  prompts: string[];
  fields: OutlineField[];
}

export const CALL_OUTLINE: OutlineStage[] = [
  {
    number: 1,
    title: "Framing",
    purpose: "Agree what this call is for, so neither side is guessing.",
    prompts: [
      "Thank them for the time, and confirm how long you have.",
      "Say what you hope to leave with, and ask what they hope to leave with.",
      "How did you come to call us?",
    ],
    fields: [
      {
        key: "theirAttendees",
        label: "Who is on the call, their side",
        kind: "short",
        hint: "Names and roles. Note anyone who joined unannounced.",
      },
      {
        key: "purposeAgreed",
        label: "What you both agreed this call is for",
        kind: "long",
      },
      {
        key: "howTheyFoundUs",
        label: "How they came to us",
        kind: "short",
        hint: "A referral is worth recording by name.",
      },
    ],
  },
  {
    number: 2,
    title: "The situation",
    purpose:
      "Get one concrete, recent instance. Not the category of problem — the last time it actually happened.",
    prompts: [
      "What prompted you to look at this now?",
      "Walk me through the last time this became a problem.",
      "Who feels it, and what do they do about it today?",
      "What have you already tried? What did you learn from it?",
    ],
    fields: [
      {
        key: "promptedNow",
        label: "What prompted this now",
        kind: "long",
        hint: "Why this month and not last year. Usually the real brief.",
      },
      {
        key: "problemInTheirWords",
        label: "The problem, in their own words",
        kind: "long",
        required: true,
        hint: "Their phrasing, not yours. Quote them where you can.",
      },
      { key: "whoFeelsIt", label: "Who feels it, and how they cope today", kind: "long" },
      {
        key: "alreadyTried",
        label: "What they have already tried",
        kind: "long",
        hint: "And what it taught them — this is where scope gets decided.",
      },
    ],
  },
  {
    number: 3,
    title: "The outcome",
    purpose: "Move from the problem to the change. What is different afterwards?",
    prompts: [
      "What must be true when this piece of work is finished?",
      "How would you know it had worked? What would you be looking at?",
    ],
    fields: [
      {
        key: "mustBeTrue",
        label: "What must be true when this is done",
        kind: "long",
        required: true,
      },
      {
        key: "successMeasures",
        label: "How they would measure it",
        kind: "long",
        hint: "A number, a report, a decision they could finally make.",
      },
    ],
  },
  {
    number: 4,
    title: "Constraints",
    purpose:
      "Timing, money and dependencies. Ask plainly; a range with an honest 'undecided' beats an invented figure.",
    prompts: [
      "Which date matters, and what makes that date matter?",
      "Do you have a budget range in mind for this? It is fine if not.",
      "What would the work depend on from your side — access, data, people?",
    ],
    fields: [
      { key: "targetDate", label: "The date that matters", kind: "date" },
      {
        key: "dateMattersBecause",
        label: "Why that date",
        kind: "long",
        hint: "A board meeting and a regulator are different problems.",
      },
      {
        key: "dependsOn",
        label: "What the work depends on from them",
        kind: "long",
        hint: "Access, data, internal sign-offs, somebody's time.",
      },
    ],
  },
  {
    number: 5,
    title: "The decision",
    purpose:
      "How this actually gets agreed. The person nobody mentions is the one who blocks it later.",
    prompts: [
      "Who signs something like this off?",
      "Walk me through how a decision like this usually gets made here.",
      "Who else will want a view before it is agreed?",
      "Are you looking at other ways of solving this?",
    ],
    fields: [
      { key: "whoSigns", label: "Who signs", kind: "short" },
      {
        key: "decisionProcess",
        label: "How the decision gets made",
        kind: "long",
        required: true,
        hint: "Steps and rough timing, not just a name.",
      },
      {
        key: "othersInTheRoom",
        label: "Who else will have a view",
        kind: "long",
        hint: "Procurement, legal, a sceptical head of operations.",
      },
      {
        key: "alsoConsidering",
        label: "What else they are considering",
        kind: "long",
        hint: "Including doing nothing, which is usually the real competitor.",
      },
    ],
  },
  {
    number: 6,
    title: "Recap and next step",
    purpose:
      "Read it back before you leave the call. Everything after this depends on having heard it right.",
    prompts: [
      "Read the recap below back to them, out loud, and let them correct it.",
      "Say what you still need, and who can get it to you.",
      "Agree exactly one next step, who owns it, and by when.",
    ],
    fields: [
      {
        key: "recap",
        label: "The recap you read back",
        kind: "long",
        hint: "Use the draft below the form, then edit it to what you said.",
      },
      {
        key: "openQuestions",
        label: "What you still need",
        kind: "long",
        hint: "And who you are expecting it from.",
      },
      {
        key: "nextStep",
        label: "The next step you agreed",
        kind: "long",
        required: true,
        hint: "One thing. Two next steps is none.",
      },
      { key: "nextStepBy", label: "By when", kind: "date" },
    ],
  },
];

/** The four the database refuses to complete a call without. */
export const REQUIRED_FIELDS = CALL_OUTLINE.flatMap((s) =>
  s.fields.filter((f) => f.required).map((f) => f),
);

/** Which required fields are still blank. Drives the Complete button. */
export function missingAnswers(call: IntroCall): OutlineField[] {
  return REQUIRED_FIELDS.filter((f) => {
    const value = call[f.key];
    return typeof value !== "string" || value.trim() === "";
  });
}

/**
 * The closing sentence, drafted from what was captured.
 *
 * Offered as a *draft* for the recap box rather than written into the record:
 * what matters is what the consultant actually said out loud and the client
 * actually corrected, and a sentence the platform composed and nobody read is
 * exactly the kind of tidy falsehood this whole subsystem exists to avoid.
 */
export function draftRecap(call: IntroCall): string {
  const or = (v: string | null, fallback: string) =>
    v && v.trim() ? v.trim() : fallback;

  const constraints: string[] = [];
  if (call.targetDate) constraints.push(`a date of ${call.targetDate}`);
  if (!call.budgetUndecided && call.budgetLow !== null) {
    const money = (minor: number) =>
      (minor / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
    constraints.push(
      call.budgetHigh !== null
        ? `a budget of ${call.budgetCurrency} ${money(call.budgetLow)}–${money(call.budgetHigh)}`
        : `a budget from ${call.budgetCurrency} ${money(call.budgetLow)}`,
    );
  }
  if (call.dependsOn?.trim()) constraints.push(call.dependsOn.trim());

  return [
    `We heard: ${or(call.problemInTheirWords, "…")}`,
    `A useful outcome would be: ${or(call.mustBeTrue, "…")}`,
    `The main constraints are: ${
      constraints.length > 0 ? constraints.join("; ") : "…"
    }.`,
    `We still need: ${or(call.openQuestions, "nothing further")}.`,
    `Our next step is: ${or(call.nextStep, "…")}${
      call.nextStepBy ? `, by ${call.nextStepBy}` : ""
    }.`,
  ].join("\n\n");
}
